import { useState } from 'react'
import { useBusiness, useUpdateBusiness, useDeleteBusiness } from '@/hooks/useBusiness'
import { BusinessEditForm } from './BusinessEditForm'
import type { UpdateBusinessData } from '@/types'

interface BusinessDetailProps {
  businessId: string
  onBack: () => void
}

export function BusinessDetail({ businessId, onBack }: BusinessDetailProps) {
  const { data: business, isLoading, error } = useBusiness(businessId)
  const updateMutation = useUpdateBusiness()
  const deleteMutation = useDeleteBusiness()
  const [isEditing, setIsEditing] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleSave = (data: UpdateBusinessData) => {
    updateMutation.mutate(
      { id: businessId, data },
      {
        onSuccess: () => {
          setIsEditing(false)
          setSaveSuccess(true)
          setTimeout(() => setSaveSuccess(false), 3000)
        },
      }
    )
  }

  const handleDelete = () => {
    deleteMutation.mutate(businessId, {
      onSuccess: () => {
        onBack()
      },
    })
  }

  if (isLoading) {
    return (
      <div className="p-6" data-testid="detail-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    )
  }

  if (error || !business) {
    return (
      <div className="p-6" data-testid="detail-error">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load business</p>
          <button
            onClick={onBack}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6" data-testid="business-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700"
            data-testid="back-btn"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h2 className="text-xl font-semibold text-gray-900">{business.name}</h2>
        </div>
        {!isEditing && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50"
              data-testid="delete-btn"
            >
              Delete
            </button>
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              data-testid="edit-btn"
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {/* Success message */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="success-message">
          <p className="text-green-800">Business updated successfully</p>
        </div>
      )}

      {/* Error message */}
      {updateMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="error-message">
          <p className="text-red-800">Failed to update business</p>
        </div>
      )}

      {/* Delete error message */}
      {deleteMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="delete-error-message">
          <p className="text-red-800">
            Failed to delete business. It may have associated documents or suppliers.
          </p>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" data-testid="delete-confirm-modal">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Business</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{business.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {isEditing ? (
          <BusinessEditForm
            business={business}
            onSave={handleSave}
            onCancel={() => setIsEditing(false)}
            isSaving={updateMutation.isPending}
          />
        ) : (
          <div className="space-y-6" data-testid="business-view">
            {/* Name */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Business Name</h3>
              <p className="mt-1 text-lg text-gray-900">{business.name}</p>
            </div>

            {/* Accounting Identifier */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Accounting Identifier</h3>
              <p className="mt-1 text-sm text-gray-900">
                {business.accounting_identifier || '—'}
              </p>
            </div>

            {/* Alternative Names */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Alternative Names</h3>
              {business.alternative_names?.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {business.alternative_names.map((name, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-sm text-gray-400">No alternative names</p>
              )}
            </div>

            {/* Credit Card Endings */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Credit Card Endings</h3>
              {business.credit_card_endings?.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {business.credit_card_endings.map((ending, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 font-mono"
                    >
                      ****{ending}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-sm text-gray-400">No credit card endings</p>
              )}
            </div>

            {/* Metadata */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Metadata</h3>
              {business.metadata && Object.keys(business.metadata).length > 0 ? (
                <pre className="mt-2 p-3 bg-gray-50 rounded-md text-sm font-mono overflow-x-auto">
                  {JSON.stringify(business.metadata, null, 2)}
                </pre>
              ) : (
                <p className="mt-1 text-sm text-gray-400">No metadata</p>
              )}
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Created</h3>
                <p className="mt-1 text-sm text-gray-700">
                  {business.created_at
                    ? new Date(business.created_at).toLocaleString()
                    : '—'}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                <p className="mt-1 text-sm text-gray-700">
                  {business.updated_at
                    ? new Date(business.updated_at).toLocaleString()
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
